import { AgGridFilterAggregate, ExternalFilter, WhereAggregateOperator } from '@anduin/shared';
import moment from 'moment';
import { TransactionsTableExternalFilters } from '../../models';

/** Accepts `YYYY-MM-DD` (NGXS default) or `MM/DD/YYYY` (datepicker format); returns `null` for empty / unparseable input. */
export function toIsoDate(value: string | null | undefined): string | null {
  if (!value) return null;
  const m = moment(value, ['YYYY-MM-DD', 'MM/DD/YYYY'], true);
  return m.isValid() ? m.format('YYYY-MM-DD') : null;
}

const MIDNIGHT_ISO_PATTERN = /T00:00(:00(\.\d+)?)?(Z|[+-]\d{2}:\d{2})?$/;
const TRAILING_ZONE_PATTERN = /(Z|[+-]\d{2}:\d{2})$/;
const ISO_DATE_PART = /^(\d{4})-(\d{2})-(\d{2})/;

/** `true` for an ISO timestamp whose time component is midnight — tolerates missing seconds / fractional / zone. */
export function isMidnightIso(value: string | null | undefined): boolean {
  return !!value && MIDNIGHT_ISO_PATTERN.test(value);
}

/**
 * Normalizes an AR backend datetime string for display in the Trans. Date / Deposit Date columns.
 * Midnight timestamps get their trailing zone stripped so the value is parsed as local time
 * (prevents UTC-midnight from rendering as the previous day in negative-offset timezones).
 * Non-midnight timestamps pass through unchanged — their zone carries real time-of-day meaning.
 */
export function normalizeArDateForDisplay(value: string | null | undefined): string | null | undefined {
  if (!value) return value;
  return isMidnightIso(value) ? value.replace(TRAILING_ZONE_PATTERN, '') : value;
}

/**
 * Resolves a legacy Hasura `transactionDate` to the firm's calendar date.
 * The Hasura `vw_ar_tran` view emits real instants (e.g. Stripe payments) as zone-less UTC
 * (no `Z`/offset); the queryable endpoint already returns a resolved date, so this only
 * applies to the Hasura path. The instant is converted to the firm timezone and re-expressed
 * as a zone-less midnight so the existing display pipeline renders that exact day for every
 * viewer. Pure manual entries (midnight / date-only) are already the firm's wall date and are
 * kept verbatim — converting them through a timezone would shift the day.
 */
export function mapHasuraArTransactionDate(value: string | null | undefined, firmTimeZone: string): string | null | undefined {
  if (!value) return value;
  if (isMidnightIso(value)) {
    const parts = ISO_DATE_PART.exec(value);
    return parts ? `${parts[1]}-${parts[2]}-${parts[3]}T00:00:00` : value;
  }
  const instant = new Date(TRAILING_ZONE_PATTERN.test(value) ? value : `${value}Z`);
  if (Number.isNaN(instant.getTime())) return value;
  const formatted = new Intl.DateTimeFormat('en-US', {
    timeZone: firmTimeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(instant);
  const part = (type: string) => formatted.find((p) => p.type === type)?.value ?? '';
  return `${part('year')}-${part('month')}-${part('day')}T00:00:00`;
}

export function marshalExternalFilters(filters: TransactionsTableExternalFilters): AgGridFilterAggregate {
  const conditions: ExternalFilter[] = [];

  const startOf = (date: string, plusDays = 0) => moment(date).add(plusDays, 'day').startOf('day').format('YYYY-MM-DDTHH:mm:ss.SSS');
  const momentMin = moment(filters.dateRange.min);
  const momentMax = moment(filters.dateRange.max);
  const dateMin = momentMin.isValid() ? momentMin.format('YYYY-MM-DD') : null;
  const dateMax = momentMax.isValid() ? momentMax.format('YYYY-MM-DD') : null;

  if (dateMin && dateMax) {
    conditions.push({
      operator: WhereAggregateOperator.OR,
      conditions: [
        {
          operator: WhereAggregateOperator.AND,
          conditions: [
            {
              column: 'transactionDate',
              filter: { filterType: 'date', type: 'inRange', filter: dateMin, filterTo: dateMax },
            },
            {
              column: 'transactionDate',
              filter: { filterType: 'date', type: 'strictNotEqual', filter: startOf(dateMax, 1) },
            },
          ],
        },
        { column: 'transactionDate', filter: { filterType: 'date', type: 'strictEquals', filter: startOf(dateMin) } },
      ],
    });
  } else {
    if (dateMin) {
      conditions.push({
        operator: WhereAggregateOperator.OR,
        conditions: [
          { column: 'transactionDate', filter: { filterType: 'date', type: 'greaterThanOrEqual', filter: dateMin } },
          { column: 'transactionDate', filter: { filterType: 'date', type: 'strictEquals', filter: startOf(dateMin) } },
        ],
      });
    }

    if (dateMax) {
      conditions.push({
        operator: WhereAggregateOperator.AND,
        conditions: [
          { column: 'transactionDate', filter: { filterType: 'date', type: 'lessThanOrEqual', filter: dateMax } },
          {
            column: 'transactionDate',
            filter: { filterType: 'date', type: 'strictNotEqual', filter: startOf(dateMax, 1) },
          },
        ],
      });
    }
  }

  if (filters.allocationStatuses.length) {
    conditions.push({
      column: 'allocationStatus',
      filter: { filterType: 'array', type: 'in', filter: filters.allocationStatuses },
    });
  }

  if (filters.clientIds.length) {
    conditions.push({
      column: 'clientId',
      filter: { filterType: 'array', type: 'in', filter: filters.clientIds },
    });
  }

  return conditions.length > 0 ? { operator: WhereAggregateOperator.AND, conditions } : undefined;
}
